/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { IUser, ServiceStatusEnum, StandardPathwayLesson } from "@imaginelearning/mps-api-service";
import {
  classMap,
  loadSpeechstream,
  UxAlert,
  UxAvatarEnum,
  UxAvatarPriorityEnum,
  UxAvatarStyleEnum,
  UxIconEnum,
  UxIconLibraryEnum,
  UxOrientationEnum,
  UxPriorityEnum,
  UxShadesEnum,
  UxSizeEnum,
  UxSizeExtendedEnum,
  UxSizeExtraEnum,
  UxStateEnum,
  UxStatusEnum,
  UxVisualActionContainer,
  UxStyleEnum,
  UxAvatar
} from "@imaginelearning/mps-ux-component-library";
import NoLessonsModal from "components/modals/no-lessons/no-lessons-modal";
import { PathwayContext } from "context-providers/pathway.provider";
import EdgePathwayActivity from "components/edge-pathway-activity/edge-pathway-activity";
import useGradeRange from "../../hooks/usegraderange";
import { UserContext } from "../../context-providers/user.provider";
import { isValidObject } from "../../helpers/verify-object";
import defaultLoadingActionProp from "../../shared/default-action";
import EdgeBackButton from "../edge-back-button/edge-back-button";
import "./edge-pathways.scss";
import useHomePage from "../../hooks/useHomePage";
import useTheme from "../../hooks/usetheme";
import SmallScreenSubheader from "components/small-screen-subheader/small-screen-subheader";

interface IProps {
  authTokenSubject?: string;
}

const intersectionObserverOptions = {
  root: null,
  rootMargin: "150px",
  threshold: 0
};

function EdgePathways(props: IProps) {
  const { authTokenSubject } = props;
  const { t, i18n } = useTranslation();
  const [showBackToCurrent, setShowBackToCurrent] = useState(false);
  const [focused, setFocused] = useState(false);
  const [chevronUp, setChevronUp] = useState(false);
  const [launchActivity, setLaunchActivity] = useState(false);
  const [lessonId, setLessonId] = useState("");
  const [lessonVersion, setLessonVersion] = useState(0);
  const [lessonTitle, setLessonTitle] = useState("");
  const [lessonAttemptId, setLessonAttemptId] = useState("");
  const [calculatorType, setCalculatorType] = useState("");
  const navigate = useNavigate();
  const { pathway, showTokenExpiredAlert, setShowTokenExpiredAlert, fetchCurrentActivity } = useContext(PathwayContext);
  const pathwaySubject: string = authTokenSubject || "";
  const focusRef = useRef<HTMLDivElement>();
  const { userState } = useContext(UserContext);
  const user: IUser = userState.response as IUser;
  const { theme } = useTheme();
  const { isKto2, is3to5, is6to8, is6to12 } = useGradeRange();
  const browserTitle = document.querySelectorAll("head title")[0];
  const titleText = `${pathwaySubject} Pathways | ${process.env.REACT_APP_WEBSITE_NAME}`;
  const { goHome } = useHomePage();
  const isSpanish =
    user?.spanishLanguage?.includes(pathwaySubject.toLowerCase()) || user?.spanishLanguage?.includes(pathwaySubject);

  if (browserTitle) {
    browserTitle.innerHTML = titleText;
  }

  useEffect(() => {
    try {
      loadSpeechstream();
    } catch (error) {
      console.error("Error loading speechstream:", error);
    }
  }, []);

  useLayoutEffect(() => {
    if (!i18n) {
      return;
    }
    if (user.spanishLanguage?.includes(pathwaySubject.toLowerCase())) {
      i18n.changeLanguage("es");
    } else {
      i18n.changeLanguage("en");
    }
  }, [i18n, user.spanishLanguage, pathwaySubject]);

  const handleClick = async (studentId: string, studentPathwayId: string) => {
    if (showTokenExpiredAlert) {
      setShowTokenExpiredAlert(false);
    }
    if (pathway.status !== ServiceStatusEnum.ERROR) {
      await fetchCurrentActivity(studentId, studentPathwayId);
      setLaunchActivity(true);
    }
  };

  const smallLoadingActionProp = {
    ...defaultLoadingActionProp,
    size: UxSizeEnum.SMALL as any
  };

  const getChildrenFromPayload = (lessons: StandardPathwayLesson[]) => {
    const isMobileDevice = window.innerWidth <= 767;

    return lessons.map((lesson: StandardPathwayLesson) => {
      const { isCurrent } = lesson;
      const isComplete: boolean = !isCurrent && lesson.isCompleted;
      const actionIcon: UxIconEnum = isComplete ? UxIconEnum.CHECK_CIRCLE : UxIconEnum.LOCK;
      const currentPriority: UxPriorityEnum = isCurrent ? UxPriorityEnum.SUCCESS : UxPriorityEnum.SECONDARY;
      const currentState: UxStateEnum = isCurrent ? UxStateEnum.INTERACTIVE : UxStateEnum.LOCKED;
      const state: UxStateEnum = isComplete ? UxStateEnum.COMPLETE : currentState;

      const lessonDisplayTitle: string = isSpanish ? (lesson?.spanishTitle ?? lesson?.title) : lesson?.title;

      let currentIcon = {};
      if (is6to12) {
        currentIcon = {
          icon: UxIconEnum.MAP_MARKER_ALT,
          library: UxIconLibraryEnum.REGULAR,
          size: UxSizeExtendedEnum.X2,
          className: classMap("current-icon text-white bg-blue rounded-circle p-2"),
          squared: true,
          theme
        };
      } else if (is3to5) {
        currentIcon = {
          icon: UxIconEnum.MATH_ICON_3_5,
          size: UxSizeExtendedEnum.X2,
          className: is3to5 && pathwaySubject === "Science" ? "science_icon_white" : "",
          theme
        };
      } else {
        currentIcon = {
          icon: UxIconEnum.MATH_ICON_K_2,
          size: UxSizeExtendedEnum.X2,
          className: isKto2 && pathwaySubject === "Science" ? "science_icon_white" : "",
          theme
        };
      }

      const nonCurrentIcon = {
        icon: UxIconEnum.CHECK,
        size: UxSizeExtendedEnum.X1,
        className: classMap(
          "completed-icon text-light rounded-circle p-2 border-gray",
          !isComplete ? "bg-light" : "bg-success"
        ),
        squared: true,
        theme
      };

      let icon;
      if (isCurrent) {
        icon = currentIcon;
      } else if (is6to12) {
        icon = nonCurrentIcon;
      }

      let size;
      let priority;
      let borderClass = "animation border-ease";
      let bodyClass = "animation align-after border-ease";
      let marginClass = "mx-auto";
      if (is6to12) {
        borderClass = isCurrent ? "border-success" : "border-white";
        bodyClass = "m-0 mw-100";
        marginClass = "p-3 py-4";
      } else {
        size = isCurrent ? UxSizeExtraEnum.XL : UxSizeEnum.LARGE;
        priority = UxShadesEnum.LIGHT;
      }

      let actionText;
      if (isCurrent) {
        actionText = is6to12 ? t("Start") : t("Go");
      }

      let statusClass = "locked";
      if (isCurrent) {
        statusClass = "current";
      } else if (isComplete) {
        statusClass = "completed";
      }

      return {
        ...smallLoadingActionProp,
        key: lesson.lessonId,
        size,
        priority,
        content: {
          state,
          icon,
          className: classMap(
            "align-items-center",
            is6to12 ? "justify-content-between" : "flex-column justify-content-center"
          ),
          title: lessonDisplayTitle,
          titleClassName: classMap(
            is6to12 && isCurrent ? "text-xl left-text" : "text-lg",
            is6to12 && isComplete ? "text-white" : ""
          ),
          action: {
            enabled: isCurrent,
            priority: isComplete || is6to12 ? UxPriorityEnum.SUCCESS : currentPriority,
            text: actionText,
            callback: () => {
              setLessonId(lesson.lessonId);
              setLessonVersion(lesson.version);
              setLessonTitle(lesson.title);
              setLessonAttemptId(lesson.attemptId);
              setCalculatorType(lesson.calculatorType);
              handleClick(user.id, pathway.response.studentPathwayId);
            },
            style: is6to12 ? undefined : UxStyleEnum.ROUNDED,
            tabIndex: 0,
            className: classMap(
              "shadow-sm mt-3 start-btn",
              isCurrent ? "px-5" : "",
              isMobileDevice ? "mobile-device" : "",
              is6to12 ? "" : "mx-auto"
            ),
            icon: {
              icon: isCurrent ? UxIconEnum.ARROW_RIGHT : actionIcon,
              size: isComplete ? UxSizeExtendedEnum.X2 : UxSizeExtendedEnum.X1,
              className: is6to12 ? "d-none" : "",
              theme
            }
          },
          ...(is6to12 && !isCurrent ? { action: undefined } : {}),
          ...(is6to8 && isCurrent
            ? {
              avatar: {
                className: "active-avatar",
                avatar: user.selectedAvatar as UxAvatarEnum,
                avatarType: UxAvatarPriorityEnum.DEFAULT,
                size: UxSizeExtendedEnum.X4,
                avatarStyle: UxAvatarStyleEnum.BODY,
                rotation: 90,
                theme
              }
            }
            : {})
        },
        status: UxStatusEnum.LOADED,
        state,
        className: classMap("lesson-card", borderClass, statusClass, marginClass),
        bodyClassName: classMap(bodyClass),
        focused: isCurrent,
        leftAlign: true,
        showOverflow: true,
        style: UxStyleEnum.SQUARED
      };
    });
  };

  const noLessonsError =
    pathway.status === ServiceStatusEnum.ERROR ||
    (pathway.status === ServiceStatusEnum.LOADED && !(isValidObject(pathway) && pathway.response.lessons));

  const getContainerChildren = () => {
    let children;
    const errorActionProp = {
      ...smallLoadingActionProp,
      status: UxStatusEnum.ERROR
    };
    switch (true) {
      case pathway.status === ServiceStatusEnum.ERROR:
        children = [
          { ...errorActionProp },
          { ...errorActionProp, size: UxSizeEnum.MEDIUM as any },
          { ...errorActionProp }
        ];
        break;
      case pathway.status === ServiceStatusEnum.LOADING:
        children = [
          { ...smallLoadingActionProp },
          { ...smallLoadingActionProp, size: UxSizeEnum.MEDIUM as any },
          { ...smallLoadingActionProp }
        ];
        break;
      case pathway.status === ServiceStatusEnum.LOADED:
        if (isValidObject(pathway.response) && pathway.response.lessons) {
          children = getChildrenFromPayload(pathway.response.lessons);
        } else {
          children = [
            { ...errorActionProp },
            { ...errorActionProp, size: UxSizeEnum.MEDIUM as any },
            { ...errorActionProp }
          ];
        }
        break;
      default:
        break;
    }
    return children;
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.intersectionRatio === 0) {
        setChevronUp(entry.boundingClientRect.y < 0);
        setShowBackToCurrent(true);
      } else {
        setShowBackToCurrent(false);
      }
    });
  }, intersectionObserverOptions);

  const setVisualActionFocus = useCallback(() => {
    if (focusRef && focusRef.current) {
      observer.observe(focusRef.current);
      const visualActionBody = document.getElementById("edge-pathways-container") as HTMLDivElement;
      const goButtons = focusRef.current.getElementsByTagName("button");
      if (visualActionBody === null) {
        return;
      }
      visualActionBody.scrollTo(0, focusRef.current.offsetTop - window.innerHeight / 3);
      goButtons[0]?.focus();
      setFocused(true);
    }
  }, [focusRef, observer]);

  useLayoutEffect(() => {
    if (!focused) {
      setVisualActionFocus();
    }
  }, [focusRef, focused, setVisualActionFocus]);

  useEffect(() => {
    if (!pathway) {
      console.warn("No pathway found");
      navigate("/");
    }
  }, [pathway, navigate]);

  const handlePendoKeyDown = useCallback((event) => {
    // @ts-ignore
    if (event.altKey && event.shiftKey && event.code === "Digit7" && window.pendo) {
      // @ts-ignore
      window.pendo.designerv2.launchInAppDesigner();
    }
  }, []);

  useEffect(() => {
    // attaching the keyboard short cut for pendo
    document.addEventListener("keydown", handlePendoKeyDown);

    // Removing the event listener
    return () => {
      document.removeEventListener("keydown", handlePendoKeyDown);
    };
  }, [handlePendoKeyDown]);

  return (
    <>
      {launchActivity ? (
        <EdgePathwayActivity
          lessonId={lessonId}
          lessonVersion={lessonVersion}
          lessonTitle={lessonTitle}
          lessonAttemptId={lessonAttemptId}
          isSpanish={isSpanish}
          calculatorType={calculatorType}
        />
      ) : (
        <>
          <SmallScreenSubheader onBackClick={goHome} />
          <Container
            id="edge-pathways-container"
            className={classMap("edge-pathways-container", is6to12 ? "mw-100" : "")}
          >
            <Row>
              <div className="me-auto ms-3 col-2 edge-back-button-holder">
                <EdgeBackButton onBackClick={() => goHome()} />
              </div>
              {showTokenExpiredAlert && (
                <div className="token-expired">
                  <UxAlert
                    priority={UxPriorityEnum.DANGER}
                    handleClose={() => setShowTokenExpiredAlert(false)}
                    dismissible={true}
                    body={t("You have been working on that lesson for a while")}
                  />
                </div>
              )}
            </Row>
            {noLessonsError ? (
              <Row>
                <NoLessonsModal showModal={true} />
              </Row>
            ) : (
              <div className="edge-pathways-list-row">
                {(isKto2 || is3to5) && user.selectedAvatar && userState.status === ServiceStatusEnum.LOADED ? (
                  <div className="fixed-avatar">
                    <UxAvatar
                      avatar={user.selectedAvatar as UxAvatarEnum}
                      avatarType={UxAvatarPriorityEnum.VEHICLE}
                      size={UxSizeExtendedEnum.X5}
                      avatarStyle={UxAvatarStyleEnum.BODY}
                      className="fixed-avatar-icon"
                      theme={theme}
                    />
                  </div>
                ) : null}
                <UxVisualActionContainer
                  items={getContainerChildren()}
                  className={classMap(
                    "w-100 text-center course-map-pathway edge-pathways-list",
                    is6to12 ? "align-items-center overflow-hidden px-5" : "course-map-limited"
                  )}
                  orientation={UxOrientationEnum.VERTICAL}
                  staggered={!is6to12}
                  focusRef={focusRef}
                  showBackToCurrent={showBackToCurrent}
                  chevronUp={chevronUp}
                  callback={setVisualActionFocus}
                  colCount={1}
                  backToCurrentButtonText={t("Back to Current")}
                />
              </div>
            )}
          </Container>
        </>
      )}
    </>
  );
}
export default EdgePathways;
